import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, FieldError, FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";

import Button from "components/Button";
import ChunkUploadFile from "components/ChunkUploadFile";
import GoogleAutoComplete from "components/GoogleAutoComplete";
import Input from "components/Input";
import Modal from "components/Modal";
import PhoneNumber from "components/PhoneNumber";
import Switch from "components/Switch";
import Toggle from "components/Toggle";
import { CUSTOMER_TRANS_ID_TYPES } from "constants/haulpay-broker";
import { UPLOAD_DOCUMENT_URLS } from "constants/routes";
import UploadItem from "features/main/Loads/modules/UploadItem";
import { phoneRegex } from "helpers/regex";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { UploadedFile } from "models/Component";
import { IOption } from "models/Form";
import { ICompany, ICustomerProfileFormData } from "models/HaulPay";
import { AddressInfo } from "models/User";
import { addNewCustomerProfileAPI, searchCompanyAPI } from "services/api/haulpay";
import { getUserByFmcsaAPI } from "services/api/user";
import { AppDispatch } from "store";
import { handleLoading } from "store/global/loader";
import { selectFactoringProfile } from "store/user/selector";
import DollarInput from "components/DollarInput";

interface IProps {
  isAddModalOpen: boolean;
  toggleAddModal: VoidFunction;
  updateCustomerList?: VoidFunction;
  onSubmitCallback?: (item: ICompany) => void;
}

const customerProfileSchema = (transIdType: IOption) =>
  Yup.object<Partial<ICustomerProfileFormData>>({
    trans_id_number: Yup.string()
      .when([], {
        is: () => transIdType.value !== "tax_id",
        then: (schema) => schema.required("This field is required"),
        otherwise: (schema) => schema.notRequired()
      })
      .when([], {
        is: () => transIdType.value === "mc",
        then: (schema) => schema.max(8, "is too long (maximum is 8 characters)"),
        otherwise: (schema) => schema
      })
      .test(
        "length-9-if-tax-or-duns",
        "is the wrong length (should be 9 characters)",
        function (value) {
          if (
            (transIdType.value === "tax_id" || transIdType.value === "duns") &&
            value &&
            value.length > 0
          ) {
            return value.length === 9;
          }
          return true;
        }
      ),
    company_name: Yup.string().required("This field is required"),
    company_address: Yup.object().required("This field is required"),
    phone_number: Yup.string()
      .required("This field is required")
      .max(19, "Phone number limit exceeded")
      .matches(phoneRegex, "Phone number is not valid"),
    email: Yup.string()
      .required("This field is required")
      .email("This field value must be a valid email"),
    noa_contact_email: Yup.string().email("This field value must be a valid email"),
    payment_status_email: Yup.string().email("This field value must be a valid email")
  });

export default function AddCustomerModal({
  isAddModalOpen,
  toggleAddModal,
  updateCustomerList,
  onSubmitCallback
}: Readonly<IProps>) {
  const dispatch = useDispatch<AppDispatch>();
  const factoringProfile = useSelector(selectFactoringProfile);
  const [customerId, setCustomerId] = useState("");
  const customerIdRef = useRef("");
  const submissionRef = useRef(0);
  const [awaitingUpload, setAwaitingUpload] = useState(false);
  const [uploaderKey, setUploaderKey] = useState(0);
  const [transIdType, setTransIdType] = useState(CUSTOMER_TRANS_ID_TYPES[0]);
  const [emailError, setEmailError] = useState<FieldError>();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [existedDebtor, setExistedDebtor] = useState<ICompany>();
  const validationSchema = useMemo(() => customerProfileSchema(transIdType), [transIdType]);

  const methods = useForm<Partial<ICustomerProfileFormData>>({
    resolver: yupResolver(validationSchema)
  });

  const {
    register,
    getValues,
    control,
    setValue,
    clearErrors,
    reset,
    formState: { errors, isSubmitting },
    handleSubmit
  } = methods;

  const resetNewCustomerForm = () => {
    if (factoringProfile?.default_do_not_contact_debtors) {
      reset({
        not_contact_debtor: true
      });
    } else {
      reset();
    }
    setEmailError(undefined);
    clearErrors();
  };

  const resetAndClose = () => {
    submissionRef.current += 1;
    customerIdRef.current = "";
    setAwaitingUpload(false);
    setCustomerId("");
    toggleAddModal();
    setUploadedFiles([]);
    setUploaderKey((key) => key + 1);
    resetNewCustomerForm();
  };

  const finishAndClose = () => {
    resetAndClose();
    updateCustomerList && updateCustomerList();
    showSuccessMessage("Successfully added customer!");
  };

  const handleUploadFailed = () => {
    if (!customerIdRef.current) return;
    resetAndClose();
    updateCustomerList && updateCustomerList();
    showErrorMessage(
      "Customer added, but the document didn't upload. Upload it from the customer's card in Customers."
    );
  };

  const onSubmit = async (customerProfileFormData: ICustomerProfileFormData) => {
    const {
      not_contact_debtor,
      company_name,
      contact_name,
      payment_status_email,
      noa_contact_email,
      phone_number,
      street_two,
      company_address: { address, city, country, state, zip },
      email,
      trans_id_number,
      notes,
      requested_credit_limit
    } = { ...customerProfileFormData };

    const customerProfileData = {
      ...(not_contact_debtor && { credit_approved: "do_not_contact" }),
      company_name,
      email,
      name: contact_name,
      [transIdType.value === "duns" ? "duns_number" : transIdType.value]: trans_id_number,
      noa_contact_email,
      payment_status_email,
      phone_number,
      notes,
      ...(requested_credit_limit && {
        requested_credit_limit: Math.round(Number(requested_credit_limit) * 100)
      }),
      address: {
        street_one: address,
        street_two: street_two,
        city,
        state,
        zip,
        country
      }
    };
    const submission = submissionRef.current;
    try {
      dispatch(handleLoading(true));
      const response = await addNewCustomerProfileAPI(customerProfileData);
      if (submission !== submissionRef.current) return;
      if (response) {
        onSubmitCallback &&
          onSubmitCallback({ ...response?.data, dba: response?.data?.company_name } as ICompany);

        if (uploadedFiles.length) {
          customerIdRef.current = response.data.id;
          setAwaitingUpload(true);
          setCustomerId(response.data.id);
          return;
        }
      }
      finishAndClose();
    } catch {
      setUploadedFiles([]);
      showErrorMessage("Error adding customer!");
    } finally {
      dispatch(handleLoading(false));
    }
  };

  const handleTransIdTypeChange = (newType: IOption) => {
    setTransIdType(newType);
    clearErrors("trans_id_number");
  };

  const getUserByFMCSA = async () => {
    const { trans_id_number } = getValues();

    if (trans_id_number && transIdType.value !== "duns") {
      try {
        dispatch(handleLoading(true));
        setEmailError(undefined);
        const response = await searchCompanyAPI("", "", { [transIdType.value]: trans_id_number });

        if (response?.data && response.data.results.length === 0) {
          const responseUserData = await getUserByFmcsaAPI({
            [transIdType.value]: trans_id_number
          } as any);

          if (responseUserData?.data) {
            const { street_one, city, country, state, zip, dba, phone } = responseUserData.data;
            const businessAddress: AddressInfo = {
              address: street_one,
              city,
              country,
              state,
              zip
            };
            setValue("company_name", dba);
            setValue("company_address", businessAddress);
            // Normalize phone to 10 digits (strip non-digits and remove leading "1" country code if present)
            const normalizedPhone = phone?.replace(/\D/g, "").replace(/^1(\d{10})$/, "$1") ?? "";
            setValue("phone_number", normalizedPhone);
          }
        } else {
          const errorMessage = `This ${transIdType.label} already exists with debtor name ${response.data.results[0].dba}, Please add a different ${transIdType.label} or `;
          setEmailError({ type: "custom", message: errorMessage });
          setExistedDebtor(response.data.results[0]);
        }
      } catch {
        setEmailError(undefined);
      } finally {
        dispatch(handleLoading(false));
      }
    } else {
      setEmailError(undefined);
    }
  };
  useEffect(() => {
    resetNewCustomerForm();
  }, []);

  return (
    <Modal
      zIndex="z-[112]"
      isOpen={isAddModalOpen}
      closeModal={resetAndClose}
      modalContainerClass="!flex !items-center sm:!items-center"
      wrapperClass="!h-full sm:!h-auto"
      dialogClass="!bg-modalBg !gap-5 !rounded-t-xl sm:!rounded-base !rounded-b-none sm:!rounded-b-base sm:!h-auto !max-w-full sm:!max-w-5xl !w-full"
      closeClass="!top-6 !right-6 !bg-transparent !text-text">
      <div className="flex flex-col flex-none gap-4 pb-3 border-b border-subtitle">
        <h4 className="text-xl font-semibold desktop:text-2xl">Add New Customer Profile</h4>
        <span>
          Add new customer profile so you can select them for payment requests.
          <br />
          If they are new to our database, we will run credit and update you by one business day.
        </span>
      </div>
      <FormProvider {...methods}>
        <form
          className="flex flex-col flex-1 overflow-auto z-90 "
          onSubmit={handleSubmit(onSubmit)}
          style={{ zIndex: 95 }}>
          <div className="flex flex-col flex-1 gap-5 overflow-auto text-xs border-b border-subtitle desktop:text-sm">
            {factoringProfile?.allow_do_not_contact_debtors ? (
              <Toggle
                className="flex items-center justify-between w-full pl-2 text-sm font-medium desktop:text-base"
                name="not_contact_debtor"
                register={register}
                prefix="Create as Do Not Contact"
              />
            ) : null}

            <div className="flex flex-col">
              <div className="flex gap-1">
                <Input
                  type="number"
                  inputMode="numeric"
                  name="trans_id_number"
                  placeholder={`Enter ${transIdType.label}`}
                  inputClass="!border-foreground h-12"
                  onBlur={async () => {
                    getUserByFMCSA();
                  }}
                  className="flex-1"
                  register={register}
                />

                <Switch
                  data={CUSTOMER_TRANS_ID_TYPES}
                  setSelectedValue={handleTransIdTypeChange}
                  isMulti
                />
              </div>

              {(errors.trans_id_number || emailError) && (
                <div className="mt-2">
                  <span className="text-sm leading-5 text-error">
                    {errors.trans_id_number?.message ?? emailError?.message}
                  </span>
                  {!errors.trans_id_number?.message && emailError?.message ? (
                    <a
                      href="#"
                      tabIndex={0}
                      onClick={(event) => {
                        event.preventDefault();
                        setEmailError(undefined);
                        onSubmitCallback && existedDebtor?.id && onSubmitCallback(existedDebtor);
                        resetAndClose();
                      }}
                      className="text-info">
                      Use this instead
                    </a>
                  ) : null}
                </div>
              )}
            </div>

            <Input
              name="company_name"
              label="* Bill To Company Name"
              register={register}
              error={errors.company_name}
            />
            <Controller
              name="company_address"
              control={control}
              render={({ field: { value = null, onChange }, fieldState: { error } }) => (
                <GoogleAutoComplete
                  label="* Bill To Company Address"
                  error={error}
                  value={value}
                  onChange={(addressInfo: AddressInfo) => onChange(addressInfo)}
                />
              )}
            />

            <Input name="street_two" label="&nbsp;Street 2" register={register} />

            <Controller
              name="phone_number"
              control={control}
              render={({ field: { value = "", onChange } }) => (
                <PhoneNumber
                  value={value}
                  onChange={onChange}
                  label="* Bill To Company Phone"
                  error={errors.phone_number}
                  wrapperClass="flex-1"
                />
              )}
            />
            <Input
              name="email"
              type="email"
              label="* Invoicing Email"
              register={register}
              error={errors.email}
            />
            <Input
              type="email"
              name="noa_contact_email"
              label="&nbsp;NOA Email"
              register={register}
              error={errors.noa_contact_email}
            />
            <Input
              type="email"
              name="payment_status_email"
              label="&nbsp;Payment Status Email"
              register={register}
              error={errors.payment_status_email}
            />
            <Input name="contact_name" label="&nbsp;Contact Name at Company" register={register} />

            <div className="flex gap-3">
              <DollarInput
                label="&nbsp;Requested Credit Limit"
                className="flex-1"
                name="requested_credit_limit"
              />
              <Input
                label="&nbsp;Notes"
                className="self-end flex-1"
                register={register}
                name="notes"
              />
            </div>

            <ChunkUploadFile
              key={uploaderKey}
              triggerVariant="button"
              className="flex-none px-5 mb-4 w-fit"
              modalZIndex="z-[115]"
              customerId={customerId}
              urlCallback={
                UPLOAD_DOCUMENT_URLS({
                  factoring_id: ""
                }).NEW_CUSTOMER
              }
              onUploadFinished={(files: any) => {
                const newCustomerFiles: UploadedFile[] = files.map((file: any) => ({
                  name: file.name,
                  size: file.fileSize,
                  type: file.fileType,
                  fileType: file.categories.split(","),
                  url: ""
                }));
                if (customerIdRef.current) {
                  finishAndClose();
                  return;
                }
                setUploadedFiles((previous) => [...previous, ...newCustomerFiles]);
              }}
              onUploadFailed={handleUploadFailed}
            />
            {uploadedFiles.map((file) => (
              <div key={file.name} className="mb-3 -mt-5 border border-primary rounded-base">
                <UploadItem file={file} hideViewButton />
              </div>
            ))}
          </div>
          <div className="flex justify-between gap-3 mt-5 desktop:justify-end">
            <Button aria-label="Cancel" type="button" variant="outline" onClick={resetAndClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting || awaitingUpload}>
              Add New Customer
            </Button>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
}
